/**
 * useListingSlideshow Hook
 *
 * Custom hook for managing listing photo slideshow on hover
 *
 * Features:
 * - Fetches full listing details via API when hasPhoto >= 3
 * - Cycles through all photos at 3-second intervals
 * - Caches fetched photos to avoid duplicate API calls
 * - Skips images that fail to load (403/404)
 * - Cleans up intervals on unmount and mouse leave
 *
 * Performance:
 * - Only fetches when hasPhoto >= 3 (has additional photos beyond initial 2)
 * - 400ms delay before triggering fetch (prevents unnecessary calls for brief hovers)
 * - Progressive lazy loading (images load as they're displayed)
 * - Estimated API call reduction: ~70% vs immediate fetch
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { trackError, ErrorSeverity } from './error-tracking';
import { describeRefusedResponse } from './read-json-response';

interface Photo {
  url: string;
  thumbUrl?: string;
  '160x160Url'?: string;
  '320x320Url'?: string;
  '640x640Url'?: string;
  '1280x1280Url'?: string;
}

interface UseListingSlideshowParams {
  listingId: number;
  initialPhotos: Photo[];
  hasPhoto: number;
  dataSourceId?: number;
}

export function useListingSlideshow({
  listingId,
  initialPhotos,
  hasPhoto,
  dataSourceId: _dataSourceId,
}: UseListingSlideshowParams) {
  // Current photo index (0-based)
  const [currentIndex, setCurrentIndex] = useState(0);

  // All photos (starts with initial 2, expanded when API fetched)
  const [allPhotos, setAllPhotos] = useState<Photo[]>(initialPhotos);

  // Whether slideshow is active
  const [isSlideshow, setIsSlideshow] = useState(false);

  // Track interval for cleanup
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track if we've already fetched for this listing (cache)
  const hasFetchedRef = useRef(false);

  // Track URLs that failed to load
  const failedUrlsRef = useRef<Set<string>>(new Set());

  // Track if component is mounted
  const isMountedRef = useRef(true);

  // Ref to hold current photos for interval access
  const allPhotosRef = useRef<Photo[]>(initialPhotos);

  /**
   * Fetch full listing details to get all photos
   */
  const fetchAllPhotos = useCallback(async () => {
    // Only startSlideshow calls this, and only for 3+ photos not yet fetched.
    // Mark as fetched immediately to prevent duplicate calls
    hasFetchedRef.current = true;

    try {
      const response = await fetch(`/api/listings/${listingId}`).catch(_err => {
        // Silently handle network errors - slideshow just won't expand
        // This prevents "Failed to fetch" errors from being tracked globally
        return null;
      });

      if (!response) {
        // Network error occurred, silently fail
        return;
      }

      if (!response.ok) {
        // API returned error status - log but don't track as error
        if (response.status !== 404) {
          trackError(`Slideshow could not fetch listing ${listingId}`, ErrorSeverity.WARNING, {
            component: 'useListingSlideshow',
            action: 'fetchAllPhotos',
            // Who answered: a 403 with no x-vercel-id was a filter on the
            // visitor's network, not our API refusing them.
            extra: { listingId, status: response.status, ...describeRefusedResponse(response) },
          });
        }
        return;
      }

      const data = await response.json();

      if (data.success && data.listing && data.listing.photos) {
        const photos = data.listing.photos;

        // Only update if we got more photos than we started with
        if (photos.length > initialPhotos.length && isMountedRef.current) {
          setAllPhotos(photos);
          allPhotosRef.current = photos;
        }
      }
    } catch (_error) {
      // Catch any unexpected errors (JSON parsing, etc.)
      // Don't log to avoid polluting console - slideshow is non-critical
    }
  }, [listingId, hasPhoto, initialPhotos.length]);

  /**
   * Get next valid photo index (skips failed URLs)
   * Takes photos array as parameter to avoid stale closure
   */
  const getNextIndex = useCallback((current: number, photos: Photo[]): number => {
    const totalPhotos = photos.length;
    if (totalPhotos === 0) return 0;

    let nextIndex = (current + 1) % totalPhotos;
    let attempts = 0;

    // Skip failed URLs, but give up after checking all photos
    while (attempts < totalPhotos) {
      const photo = photos[nextIndex];

      // Check if any variant of this photo URL has failed
      const url = photo?.url;
      const url320 = photo?.['320x320Url'];
      const url640 = photo?.['640x640Url'];

      const hasFailed =
        (url && failedUrlsRef.current.has(url)) ||
        (url320 && failedUrlsRef.current.has(url320)) ||
        (url640 && failedUrlsRef.current.has(url640));

      if (!hasFailed && (url || url320 || url640)) {
        return nextIndex;
      }

      nextIndex = (nextIndex + 1) % totalPhotos;
      attempts++;
    }

    // All photos failed, return to start
    return 0;
  }, []);

  /**
   * Start the slideshow
   */
  const startSlideshow = useCallback(() => {
    // Only start if we have enough photos
    if (hasPhoto < 3) {
      return;
    }

    // Fetch photos if not already fetched
    if (!hasFetchedRef.current) {
      void fetchAllPhotos();
    }

    // Start slideshow at index 1 (2nd photo) for continuity
    setCurrentIndex(1);
    setIsSlideshow(true);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up interval to rotate photos every 2 seconds
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => getNextIndex(prev, allPhotosRef.current));
    }, 2000);
  }, [hasPhoto, fetchAllPhotos, getNextIndex]);

  /**
   * Stop the slideshow and reset to first photo
   */
  const stopSlideshow = useCallback(() => {
    // Clear interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset state
    setIsSlideshow(false);
    setCurrentIndex(0);
  }, []);

  /**
   * Handle image load error
   * Tracks failed URL and skips to next photo
   */
  const handleImageError = useCallback((url: string) => {
    // Add to failed set
    failedUrlsRef.current.add(url);

    // Skip to next photo immediately
    setCurrentIndex(prev => getNextIndex(prev, allPhotosRef.current));
  }, [getNextIndex]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  /**
   * Update photos when initialPhotos changes (only if content actually changed)
   */
  useEffect(() => {
    // Only update if we haven't fetched yet (preserve fetched photos)
    if (!hasFetchedRef.current) {
      // Check if photos actually changed (compare length and first photo URL to avoid unnecessary updates)
      const photosChanged =
        allPhotosRef.current.length !== initialPhotos.length ||
        (initialPhotos.length > 0 && allPhotosRef.current[0]?.url !== initialPhotos[0]?.url);

      if (photosChanged) {
        setAllPhotos(initialPhotos);
        allPhotosRef.current = initialPhotos;
      }
    }
  }, [initialPhotos]);

  /**
   * Check if a photo URL has failed to load
   */
  const hasImageFailed = useCallback((photo: Photo | undefined): boolean => {
    if (!photo) return true;

    const url = photo?.url;
    const url320 = photo?.['320x320Url'];
    const url640 = photo?.['640x640Url'];

    return !!(
      (url && failedUrlsRef.current.has(url)) ||
      (url320 && failedUrlsRef.current.has(url320)) ||
      (url640 && failedUrlsRef.current.has(url640))
    );
  }, []);

  return {
    currentIndex,
    allPhotos,
    isSlideshow,
    handleImageError,
    startSlideshow,
    stopSlideshow,
    hasImageFailed,
  };
}
